import { useRef, useCallback, useEffect, useState } from 'react';
import './Editor.css';

interface EditorProps {
  code: string;
  onChange: (code: string) => void;
}

// Jertscript v2 syntax highlighting
function highlightJertscript(code: string): string {
  // Use placeholder tokens to avoid regex conflicts
  // Using \x00PH_index_PH\x00 format to avoid number regex matching the index
  const placeholders: string[] = [];
  const placeholder = (text: string, className: string) => {
    const index = placeholders.length;
    placeholders.push(`<span class="${className}">${text}</span>`);
    return `\x00PH_${index}_PH\x00`;
  };

  let highlighted = code;

  // Extract and replace strings FIRST (before HTML escaping)
  highlighted = highlighted.replace(
    /("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|`(?:[^`\\]|\\.)*`)/g,
    (match) => placeholder(match.replace(/</g, '&lt;').replace(/>/g, '&gt;'), 'syntax-string')
  );

  // Extract and replace comments
  highlighted = highlighted.replace(
    /(\/\/[^\n]*)/g,
    (match) => placeholder(match.replace(/</g, '&lt;').replace(/>/g, '&gt;'), 'syntax-comment')
  );

  // Now escape remaining HTML
  highlighted = highlighted
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Numbers
  highlighted = highlighted.replace(
    /\b(\d+\.?\d*)\b/g,
    '<span class="syntax-number">$1</span>'
  );

  // XION functions (green, underlined)
  highlighted = highlighted.replace(
    /\b(connecter|deconnecter|solde|envoyer|obtenirPortefeuille|estConnecte)\b/g,
    '<span class="syntax-xion">$1</span>'
  );

  // Builtins (underlined)
  highlighted = highlighted.replace(
    /\b(dire|crier|avertir|demander)\b/g,
    '<span class="syntax-builtin">$1</span>'
  );

  // Declaration keywords (bold white)
  highlighted = highlighted.replace(
    /\b(croissant|baguette)\b/g,
    '<span class="syntax-declaration">$1</span>'
  );

  // Control flow keywords (white)
  highlighted = highlighted.replace(
    /\b(franchement|sinon|pendant|pour|fonction|retourner|malheureusement|merde|quandmeme|lancer|casser|continuer)\b/g,
    '<span class="syntax-keyword">$1</span>'
  );

  // Async/class keywords (white)
  highlighted = highlighted.replace(
    /\b(asynchrone|attendre|classe|nouveau|ceci|constructeur)\b/g,
    '<span class="syntax-keyword">$1</span>'
  );

  // Module keywords (white)
  highlighted = highlighted.replace(
    /\b(importer|exporter|de)\b/g,
    '<span class="syntax-keyword">$1</span>'
  );

  // Type keywords (cyan/blue)
  highlighted = highlighted.replace(
    /\b(nombre|texte|booleen|quelconque|inconnu|jamais|vide|interface|type|enumeration|tableau|promesse|Promesse)\b/g,
    '<span class="syntax-type">$1</span>'
  );

  // Access modifiers (white)
  highlighted = highlighted.replace(
    /\b(prive|publique|protege|lecture_seule|etend|implemente|comme)\b/g,
    '<span class="syntax-keyword">$1</span>'
  );

  // Literals (yellow)
  highlighted = highlighted.replace(
    /\b(oui|non|rien)\b/g,
    '<span class="syntax-literal">$1</span>'
  );

  // Operators (cream)
  highlighted = highlighted.replace(
    /\b(et|ou|pas|est|nest_pas)\b/g,
    '<span class="syntax-operator">$1</span>'
  );

  // Restore placeholders
  highlighted = highlighted.replace(/\x00PH_(\d+)_PH\x00/g, (_, index) => placeholders[parseInt(index)]);

  return highlighted;
}

function Editor({ code, onChange }: EditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const highlightRef = useRef<HTMLPreElement>(null);
  const lineNumbersRef = useRef<HTMLDivElement>(null);
  const [lineCount, setLineCount] = useState(1);

  // Update line numbers
  useEffect(() => {
    const lines = code.split('\n').length;
    setLineCount(lines);
  }, [code]);

  // Sync scroll between textarea and highlight layer
  const handleScroll = useCallback(() => {
    if (textareaRef.current && highlightRef.current && lineNumbersRef.current) {
      highlightRef.current.scrollTop = textareaRef.current.scrollTop;
      highlightRef.current.scrollLeft = textareaRef.current.scrollLeft;
      lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  }, []);

  // Handle tab key to insert spaces
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const textarea = textareaRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      const newValue = code.substring(0, start) + '  ' + code.substring(end);
      onChange(newValue);

      // Restore cursor position after state update
      requestAnimationFrame(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2;
      });
    }
  }, [code, onChange]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  }, [onChange]);

  // Generate line numbers
  const lineNumbers = Array.from({ length: lineCount }, (_, i) => i + 1);

  return (
    <div className="editor">
      {/* Line Numbers */}
      <div className="editor-line-numbers" ref={lineNumbersRef}>
        {lineNumbers.map(num => (
          <div key={num} className="line-number">
            {num}
          </div>
        ))}
      </div>

      {/* Code Area */}
      <div className="editor-code-area">
        {/* Syntax Highlighting Layer */}
        <pre
          ref={highlightRef}
          className="editor-highlight"
          aria-hidden="true"
          dangerouslySetInnerHTML={{ __html: highlightJertscript(code) + '\n' }}
        />

        {/* Editable Textarea */}
        <textarea
          ref={textareaRef}
          className="editor-textarea"
          value={code}
          onChange={handleChange}
          onScroll={handleScroll}
          onKeyDown={handleKeyDown}
          spellCheck={false}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          data-gramm="false"
          placeholder="// Écrivez votre code Jertscript ici..."
        />
      </div>
    </div>
  );
}

export default Editor;
